<template>
  <div class="relative w-full flex px-px flex-col md:px-150px md:flex-row">
    <div
      class="absolute top-0 left-0 right-0 ml-auto mr-auto z-0 pro-onb-bg-pattern-mobile md:pro-onb-bg-pattern"
    />

    <page-content-context
      full-container-class="z-20 flex flex-col xl:flex-row text-14"
    >
      <div class="w-full pr-100px mt-16 px-6 md:mt-138px">
        <div class="welcome-tag py-0.5 px-3 w-min rounded-4">
          <p class="text-14 text-grayscale-96 font-semibold">Welcome!</p>
        </div>

        <h1 class="mt-8 font-druk-wide text-grayscale-96 text-20 md:text-32">
          Become an Xposure Music Pro
        </h1>

        <p class="mt-10 text-16-180 text-blue-haze mb-20">
          This application is sent to Xposure Music for review and consideration
          and all information provided is subject to our privacy policy.
        </p>
      </div>

      <div
        class="min-w-56.84 py-10 px-8 bg-transparent md:bg-woodsmoke md:shadow z-10 h-min flex flex-col md:rounded-12 md:my-106px"
      >
        <form class="w-full mt-0 md:mt-12">
          <p class="font-bold text-accent-violet text-13-140 uppercase">
            General information
          </p>

          <div class="flex mt-8 flex-col lg:flex-row">
            <input-field
              v-model:value="firstName"
              name="firstName"
              label="First name"
              type="text"
              label-class="mb-2 font-14 text-manatee"
              full-container-class="w-full"
              input-container-class="mt-2"
              :is-disabled="isLoading"
              v-model:error-message="errors.firstNameError"
              @clearError="clearError"
              @proceed="checkInput"
            />
            <input-field
              v-model:value="lastName"
              name="lastName"
              label="Last name"
              type="text"
              label-class="mb-2 font-14 text-manatee"
              full-container-class="w-available mt-6 lg:ml-2 lg:mt-0"
              input-container-class="mt-2"
              :is-disabled="isLoading"
              v-model:error-message="errors.lastNameError"
              @clearError="clearError"
              @proceed="checkInput"
            />
          </div>
          <input-field
            v-model:value="email"
            name="email"
            label="Email"
            type="text"
            label-class="mb-2 font-14 text-manatee"
            full-container-class="mt-6"
            input-container-class="mt-2"
            :is-disabled="isLoading"
            v-model:error-message="errors.emailError"
            @clearError="clearError"
            @proceed="checkInput"
          />
          <ClientOnly>
            <phone-input
              :phone="phone"
              @phoneChanged="phoneChanged"
              class="mt-6"
            />
          </ClientOnly>
          <p
            class="font-bold text-accent-violet text-13-140 uppercase mt-16 mb-12 md:mb-6"
          >
            Links
          </p>
          <SocialInputField
            v-for="key in socialLinkProvider"
            :key="key.label"
            v-model:value="socialLinks[key.value]"
            :icon="key.icon"
            :label="key.label"
            :loading="isLoading"
            :provider="key.value"
            isPro
            @clear-error="clearError"
          />
          <input-field
            v-model:value="socialLinks[ProSocialLinkProvider.personalSite]"
            :name="ProSocialLinkProvider.personalSite"
            label="Website"
            type="url"
            label-class="mb-2 font-14 text-manatee"
            full-container-class="w-available"
            input-container-class="mt-2"
            :is-disabled="isLoading"
            @clearError="clearError"
            @proceed="checkInput"
          />
          <p class="font-bold text-accent-violet text-13-140 uppercase mt-16">
            Expertise
          </p>
          <input-field
            v-model:value="worksAt"
            name="worksAt"
            label="Where do you work today?"
            type="text"
            placeholder="ex. A&R at Universal Music"
            label-class="mb-2 font-14 text-manatee"
            full-container-class="mt-12 md:mt-6"
            input-container-class="mt-2"
            :is-disabled="isLoading"
            v-model:error-message="errors.worksAtError"
            @clearError="clearError"
            @proceed="checkInput"
          />
          <input-field
            v-model:value="about"
            name="about"
            label="What else should we know about you? (optional)"
            type="textarea"
            placeholder="Notable achievements, artists you’ve worked with"
            input-type="textarea"
            label-class="mb-2 font-14 text-manatee"
            full-container-class="mt-6"
            input-container-class="mt-2"
            :is-disabled="isLoading"
            @proceed="checkInput"
          />
          <input-field
            v-model:value="referral"
            name="referral"
            label="Who (if anyone) referred you to Xposure? (optional)"
            type="text"
            label-class="mb-2 font-14 text-manatee"
            full-container-class="mt-6"
            input-container-class="mt-2"
            :is-disabled="isLoading"
            @proceed="checkInput"
          />
        </form>

        <span
          v-if="errorMessage"
          class="text-red mt-8 self-center md:self-auto"
          >{{ errorMessage }}</span
        >

        <button
          id="sign-up-button"
          :class="[
            errorMessage ? 'mt-2' : 'mt-16',
            'bg-accent-orange rounded-54 h-46 text-base text-grayscale-7 font-bold ml-auto w-full md:w-114',
          ]"
          :disabled="isLoading"
          @click="checkInput"
        >
          Apply
        </button>
      </div>
    </page-content-context>
  </div>
</template>

<script setup lang="ts">
import { useAuthenticationStore } from "@/store/authentication";
import { ApplyProError } from "@/types/authentication";
import ApiError from "@/types/error";
import { runWithDelay, scrollToFirstError } from "@/utils/helpers";
import {
  validateEmail,
  validateField,
  validateSocialLink,
} from "@/utils/validation";
import { reactive, ref } from "vue";
import PageContentContext from "@/components/contexts/PageContentContext.vue";
import InputField from "@/components/layout/inputs/InputField.vue";
import {
  ProSocialLinkProvider,
  ProSocialLinkProviderInfo,
  linkProvider,
} from "@/types/user";
import PageTitles from "@/assets/strings";
import PhoneInput from "@/components/layout/inputs/PhoneInput.vue";
import SocialInputField from "@/components/layout/inputs/SocialInputField.vue";

useHead({
  title: PageTitles.applyAsIndustryPro.title,
  meta: [
    {
      name: "description",
      content: PageTitles.applyAsIndustryPro.description,
    },
    {
      name: "og:description",
      content: PageTitles.applyAsIndustryPro.description,
    },
    {
      name: "twitter:description",
      content: PageTitles.applyAsIndustryPro.description,
    },
    {
      property: "og:title",
      content: PageTitles.applyAsIndustryPro.title,
    },
    {
      property: "twitter:title",
      content: PageTitles.applyAsIndustryPro.title,
    },
  ],
});

const router = useRouter();

const authenticationStore = useAuthenticationStore();

const errors = reactive({
  firstNameError: "",
  lastNameError: "",
  emailError: "",
  instagramError: "",
  twitterError: "",
  linkedinError: "",
  youtubeError: "",
  personalSiteError: "",
  worksAtError: "",
} as ApplyProError);

const email = ref("");
const phone = ref("");
const firstName = ref("");
const lastName = ref("");
const socialLinks = ref({
  instagram: "",
  twitter: "",
  linkedin: "",
  youtube: "",
  personalSite: "",
});
const about = ref("");
const referral = ref("");
const worksAt = ref("");
const isLoading = ref(false);
const errorMessage = ref("");

const socialLinkProvider = ProSocialLinkProviderInfo;

const phoneChanged = (data: { phone: string }) => {
  phone.value = data.phone;
};

const validateProSocialLink = (link: string, provider: linkProvider) => {
  socialLinks.value[provider] = validateSocialLink(link, provider.value, true);
};

const checkInput = async (): Promise<void> => {
  isLoading.value = true;
  errors.firstNameError = validateField(firstName.value, "first name", "input");
  errors.lastNameError = validateField(lastName.value, "last name", "input");
  errors.emailError = validateEmail(email.value);
  errors.worksAtError = validateField(worksAt.value, "workplace", "input");
  if (Object.values(errors).some(Boolean)) {
    scrollToFirstError(errors);
    isLoading.value = false;
    return;
  }

  try {
    await authenticationStore.applyPro({
      email: email.value,
      phone: phone.value,
      firstName: firstName.value,
      lastName: lastName.value,
      socialLinks: [
        socialLinks.value.instagram,
        socialLinks.value.twitter,
        socialLinks.value.linkedin,
        socialLinks.value.youtube,
        socialLinks.value.personalSite,
      ],
      about: about.value,
      referral: referral.value,
      worksAt: worksAt.value,
    });
    router.push("/apply-as-industry-pro/success");
  } catch (error) {
    errorMessage.value = (error as ApiError).message;
    runWithDelay(() => (errorMessage.value = ""));
  }

  isLoading.value = false;
};

const clearError = (inputName: keyof ApplyProError): void => {
  const fieldName = `${inputName}Error` as keyof ApplyProError;
  if (Object.prototype.hasOwnProperty.call(errors, fieldName)) {
    if (errors[fieldName]) {
      errors[fieldName] = "";
    }
  }
};
</script>

<style scoped>
.shadow {
  box-shadow: 0px 4px 64px rgba(0, 0, 0, 0.5);
}
</style>
